import { Label } from '../../ui/label';
import { cn } from '../../../lib/cn';
import {
  CHI_DELLA_LISTA,
  TRIGGER,
  type ChiDellaLista,
  type TriggerKind,
} from '../../../types/automazioni.types';
import type { EtichettaScelta } from '../../../lib/email/automationService';
import type { Partenza } from '../../../lib/email/partenzaSequenza';

interface SceltaPartenzaProps {
  valore: Partenza;
  liste: EtichettaScelta[];
  /** Sequenza di servizio: entrano anche quelli che non hanno accettato le promozioni. */
  servizio?: boolean;
  onChange: (partenza: Partenza) => void;
}

/** Una scelta fra poche: tutta la riga si clicca, il nome sopra e cosa vuol dire sotto. */
function Opzione({
  scelta,
  label,
  spiegazione,
  onClick,
}: {
  scelta: boolean;
  label: string;
  spiegazione: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={scelta}
      className={cn(
        'w-full text-left px-3 py-2 rounded-lg border transition-colors',
        scelta ? 'border-primary-400 bg-primary-50/50' : 'border-gray-200 hover:border-gray-300'
      )}
    >
      <span className="block text-xs font-semibold text-gray-900">{label}</span>
      <span className="block text-[11px] text-neutral-400 mt-0.5 leading-relaxed">
        {spiegazione}
      </span>
    </button>
  );
}

/**
 * Da dove entrano i contatti. Per la partenza da lista si sceglie anche la
 * lista, e se entra tutta o solo chi viene aggiunto dopo: e' la differenza fra
 * scrivere subito a decine di persone e scrivere a una sola.
 */
export function SceltaPartenza({ valore, liste, servizio = false, onChange }: SceltaPartenzaProps) {
  const daLista = valore.trigger === 'etichetta_aggiunta';
  const lista = liste.find((l) => l.id === valore.listaId);
  const quantiEntrano = lista ? (servizio ? lista.ricevono_servizio : lista.possono_ricevere) : 0;

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Quando ci entra un contatto</Label>
        <div className="space-y-1.5 mt-1.5">
          {(Object.keys(TRIGGER) as TriggerKind[]).map((t) => (
            <Opzione
              key={t}
              scelta={valore.trigger === t}
              label={TRIGGER[t].label}
              spiegazione={TRIGGER[t].spiegazione}
              onClick={() => onChange({ ...valore, trigger: t })}
            />
          ))}
        </div>
      </div>

      {daLista && (
        <>
          <div>
            <Label htmlFor="lista-partenza" className="text-xs">
              Quale lista <span className="text-primary-600">*</span>
            </Label>
            <select
              id="lista-partenza"
              value={valore.listaId}
              onChange={(e) => onChange({ ...valore, listaId: e.target.value })}
              className="w-full mt-1 rounded-md border border-gray-200 px-2 py-2 text-sm"
            >
              <option value="">
                {liste.length === 0 ? 'Nessuna lista: creala nella scheda Liste' : 'Scegli...'}
              </option>
              {liste.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label className="text-xs">
              A chi della lista <span className="text-primary-600">*</span>
            </Label>
            <div className="space-y-1.5 mt-1.5">
              {(Object.keys(CHI_DELLA_LISTA) as ChiDellaLista[]).map((c) => (
                <Opzione
                  key={c}
                  scelta={valore.chi === c}
                  label={CHI_DELLA_LISTA[c].label}
                  spiegazione={CHI_DELLA_LISTA[c].spiegazione}
                  onClick={() => onChange({ ...valore, chi: c })}
                />
              ))}
            </div>
            {valore.chi === 'tutta' && lista && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-2.5 py-1.5 mt-2 leading-relaxed">
                Accendendo la sequenza entrano subito {quantiEntrano}{' '}
                {quantiEntrano === 1 ? 'contatto' : 'contatti'} di "{lista.name}": quelli che
                possono ricevere. Chi l'ha gia' percorsa non rientra.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
